import { Phone, Globe, Camera, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { Empresa } from '@/api/types';

export function ScoreBadge({
  score,
  size = 'sm',
  className,
}: {
  score?: number;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  const s = score ?? 0;
  const tone =
    s >= 8
      ? 'border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200'
      : s >= 5
        ? 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
        : s >= 2
          ? 'border-slate-300 bg-slate-50 text-slate-700 dark:bg-slate-900 dark:text-slate-300'
          : 'border-rose-400/30 bg-rose-50 text-rose-900 dark:bg-rose-950 dark:text-rose-200';

  const sizeCls = {
    xs: 'text-[10px] px-1.5 py-0',
    sm: 'text-[11px] px-1.5 py-0.5',
    md: 'text-xs px-2 py-0.5',
  }[size];

  return (
    <span
      className={cn(
        'mono inline-flex items-center gap-0.5 rounded border tabular leading-none',
        tone,
        sizeCls,
        className,
      )}
      title={`Score de prospecção: ${s}/10`}
    >
      <span className="font-semibold">{s}</span>
      <span className="opacity-50">/10</span>
    </span>
  );
}

const SINAL_DEFS: Array<{
  key: keyof Pick<Empresa, 'tem_telefone' | 'tem_site' | 'tem_redes'> | 'ativa';
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
}> = [
  { key: 'tem_telefone', icon: Phone, title: 'tem telefone da Receita' },
  { key: 'tem_site', icon: Globe, title: 'tem site' },
  { key: 'tem_redes', icon: Camera, title: 'tem redes sociais' },
  { key: 'ativa', icon: CheckCircle2, title: 'situação ATIVA' },
];

export function SinaisRow({ e, className }: { e: Empresa; className?: string }) {
  const sinais = [
    { active: e.tem_telefone, def: SINAL_DEFS[0]! },
    { active: e.tem_site, def: SINAL_DEFS[1]! },
    { active: e.tem_redes, def: SINAL_DEFS[2]! },
    { active: e.situacao ? /^(ativa|active)$/i.test(e.situacao) : undefined, def: SINAL_DEFS[3]! },
  ];

  return (
    <div className={cn('inline-flex items-center gap-1', className)}>
      {sinais.map(({ active, def }) => {
        const Icon = def.icon;
        return (
          <span key={def.key} title={def.title} className="inline-flex">
            <Icon
              className={cn(
                'h-3 w-3 shrink-0 transition-colors',
                active === true && 'text-emerald-600 dark:text-emerald-400',
                active === false && 'text-muted-foreground/20',
                active === undefined && 'text-muted-foreground/30',
              )}
              strokeWidth={active ? 2.5 : 1.75}
            />
          </span>
        );
      })}
    </div>
  );
}
